import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	FormArray,
	FormBuilder,
	FormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { ExtendedPhoneNumber } from 'src/app/models/data/user';
import { ApiService } from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { NgClass } from '@angular/common';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { PhoneTypeFormComponent } from '../../../components/phones-form/phone-type-form.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
	selector: 'app-add-edit-contact',
	templateUrl: './add-edit-contact.component.html',
	styleUrls: ['./add-edit-contact.component.scss', '../../common/main.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		NgClass,
		MatFormField,
		MatInput,
		MatError,
		PhoneTypeFormComponent,
		MatSlideToggle,
		MatIcon,
		MatButton,
	],
})
export class AddEditContactComponent implements OnChanges {
	@Input() contactDetailData: any;
	@Input() saveClickedFlag!: boolean;
	@Input() clientId!: string;

	@Output() formDataSubmitted = new EventEmitter<any[]>();
	@Output() updatedListAfterAddNewRow = new EventEmitter<any[] | boolean>();

	addEditContactInformationFormGroup!: FormGroup;
	visibleIfSMSNotification: boolean = true; //* To control visibility of Phone Number field based on SMS Notification checkbox
	message: string = '';
	flagForPhoneNumberErrorMessage: boolean = false;

	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly apiService: ApiService,
		private readonly bannerMessageService: BannerMessageService,
		private readonly cdr: ChangeDetectorRef
	) {
		this.addEditContactInformationFormGroup = this.formBuilder.group({
			addEditContactDetailsArray: this.formBuilder.array([]),
		});

		this.smsNotificationFlagCheck();
		this.defaultRowAdd();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.contactDetailData && changes['contactDetailData']) {
			if (!Array.isArray(this.contactDetailData)) {
				return;
			}

			if (this.contactDetailData?.length > 0) {
				this.addEditContactDetailsArray.clear();

				this.contactDetailData?.forEach((contact) => {
					this.addEditContactDetailsArray?.push(
						this.createContactForm(contact)
					);
				});
			}

			this.updatedListAfterAddNewRow.emit(
				this.addEditContactDetailsArray.valid ??
					this.addEditContactInformationFormGroup.valid
			);
		}

		if (changes['saveClickedFlag'] && this.saveClickedFlag) {
			const formArrayData = this.addEditContactDetailsArray.value;

			if (this.addEditContactInformationFormGroup.valid) {
				this.formDataSubmitted.emit(formArrayData);
			} else {
				console.warn('Form is invalid');
				this.formDataSubmitted.emit([]);
			}
		}
	}

	private createContactForm(contact?: any): FormGroup {
		return this.formBuilder.group({
			id: [contact?.id ?? null],
			clientId: [contact?.clientId ?? null],
			firstName: [
				contact?.firstName ?? '',
				[
					Validators.required,
					Validators.minLength(1),
					Validators.maxLength(60),
				],
			],
			lastName: [
				contact?.lastName ?? '',
				[
					Validators.required,
					Validators.minLength(1),
					Validators.maxLength(60),
				],
			],
			email: [contact?.email ?? '', [Validators.email]],
			phoneNumber: [contact?.phoneNumber ?? ''],
			isActive: [Boolean(contact?.active)],
		});
	}

	get addEditContactDetailsArray(): FormArray {
		return this.addEditContactInformationFormGroup.get(
			'addEditContactDetailsArray'
		) as FormArray;
	}

	smsNotificationFlagCheck(): void {
		// Checks that the active company has the sms Notifications feature enabled :
		this.apiService
			.getCompanySettingByName('smsNotifications')
			.then((companySetting) => {
				if (companySetting?.value?.toLowerCase()?.startsWith('true')) {
					this.visibleIfSMSNotification = true;
				} else {
					this.visibleIfSMSNotification = false;
				}
			});
	}

	defaultRowAdd(event?: any): void {
		if (this.addEditContactDetailsArray.length < 5) {
			if (event) {
				event.preventDefault();
			}

			this.addEditContactDetailsArray.push(this.addRow());
			this.updatedListAfterAddNewRow.emit(
				this.addEditContactInformationFormGroup.valid
			);
		}

		// Add the toggle to active for the newly added row :
		this.addEditContactDetailsArray.at(
			this.addEditContactDetailsArray?.length - 1
		)?.value?.id
			? ''
			: this.addEditContactDetailsArray
					.at(this.addEditContactDetailsArray?.length - 1)
					?.get('isActive')!
					.setValue(true);
	}

	addRow(): FormGroup {
		return this.formBuilder.group({
			firstName: [
				'',
				[
					Validators.required,
					Validators.minLength(1),
					Validators.maxLength(60),
				],
			],
			lastName: [
				'',
				[
					Validators.required,
					Validators.minLength(1),
					Validators.maxLength(60),
				],
			],
			email: ['', [Validators.email, Validators.required]],
			phoneNumber: [null],
			isActive: [false],
		});
	}

	async removeRow(index: number): Promise<void> {
		let contactId = this.addEditContactDetailsArray.at(index)?.value?.id;
		let clientId = this.clientId.toString();

		if (this.addEditContactDetailsArray.at(index)?.value?.id) {
			this.message = '';
			let messageType: BannerMessageType;
			try {
				const postContactDetails = await this.apiService.deleteContactDetail(
					contactId,
					clientId
				);

				if (postContactDetails?.data?.message) {
					this.removeContactListAfterDeleteRecord(index);

					this.message = $localize`:@@contactDetail-remove-message:Contact deleted successfully`;
					messageType = BannerMessageType.success;
				} else {
					messageType = BannerMessageType.error;
				}
			} catch (err) {
				this.message = $localize`:@@userAbout-saveErrorMessage:Error saving changes`;
				console.error('Error saving changes: ', err);
				messageType = BannerMessageType.error;
			}

			this.bannerMessageService.sendMessage(this.message, messageType);
		} else {
			this.removeContactListAfterDeleteRecord(index);
		}
	}

	removeContactListAfterDeleteRecord(index: number): void {
		this.addEditContactDetailsArray.removeAt(index);

		this.addEditContactDetailsArray.length === 0 ? this.defaultRowAdd() : '';
		this.updatedListAfterAddNewRow.emit(
			this.addEditContactInformationFormGroup.valid
		);

		this.cdr.markForCheck();
	}

	inputValuesChange() {
		this.updatedListAfterAddNewRow.emit(
			this.addEditContactInformationFormGroup.valid
		);
	}

	updateUserPhone(phoneDetail: ExtendedPhoneNumber, index: number): void {
		if (phoneDetail.rawPhoneNumber && !phoneDetail?.onBlurFlag) {
			this.addEditContactDetailsArray
				.at(index)
				.patchValue({ phoneNumber: phoneDetail });
			this.addEditContactDetailsArray.at(index)?.value?.id
				? ''
				: this.addEditContactDetailsArray
						.at(index)
						.get('isActive')!
						.setValue(true);

			this.addEditContactDetailsArray
				.at(index)
				.get('email')!
				.removeValidators([Validators.required]);
			this.addEditContactDetailsArray
				.at(index)
				.get('email')!
				.updateValueAndValidity();

			this.updatedListAfterAddNewRow.emit(
				this.addEditContactInformationFormGroup.valid
			);

			if (this.flagForPhoneNumberErrorMessage)
				this.flagForPhoneNumberErrorMessage = false;
		} else {
			this.flagForPhoneNumberErrorMessage = true;

			this.addEditContactDetailsArray
				.at(index)
				.get('email')!
				.addValidators([Validators.required]);
			this.addEditContactDetailsArray
				.at(index)
				.get('email')!
				.updateValueAndValidity();

			if (phoneDetail.onBlurFlag)
				this.addEditContactDetailsArray
					.at(index)
					.patchValue({ phoneNumber: null });
			this.updatedListAfterAddNewRow.emit(
				this.addEditContactInformationFormGroup.valid
			);
		}
	}
}
